import { unpackToDateTime } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { FormFieldConfig, FormFieldValue, FormSubmissionFieldValue, ExpandedFormFieldConfig } from './form-responses-types';

export const parseUserAgent = (ua: string | null | undefined): { browser: string; os: string; device: string } => {
  if (!ua) return { browser: '-', os: '-', device: '-' };

  let browser = 'Unknown';
  let os = 'Unknown';
  let device = 'Desktop';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  if (ua.includes('Mobile') || ua.includes('Android')) device = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad')) device = 'Tablet';

  return { browser, os, device };
};

export const formatSubmissionDate = (dateStr: string): string => {
  const dt = unpackToDateTime(dateStr);
  const formattedDate = dt.toLocaleString(DateTime.DATE_MED);
  const formattedTime = dt.toFormat('h:mm a');
  return `${formattedDate}, ${formattedTime}`;
};

export const getFieldValue = (
  submission: { formFieldValues?: FormFieldValue[] },
  taskFieldId: string
): string => {
  const fieldValue = submission.formFieldValues?.find((fv) => fv.taskFieldId === taskFieldId);
  return fieldValue?.value || '-';
};

export const sortFieldValuesByFormOrder = (
  fieldValues: FormSubmissionFieldValue[],
  formFields: FormFieldConfig[]
): FormSubmissionFieldValue[] => {
  if (!fieldValues || !formFields.length) return fieldValues || [];
  const orderMap = new Map(formFields.map((f, idx) => [f.taskFieldId, idx]));
  return [...fieldValues].sort((a, b) => {
    const orderA = orderMap.get(a.taskFieldId) ?? 999;
    const orderB = orderMap.get(b.taskFieldId) ?? 999;
    return orderA - orderB;
  });
};

export const escapeCSV = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const getGroupInstanceIds = (
  submission: { formFieldValues?: FormFieldValue[] },
  groupFieldIds: Set<string>
): string[] => {
  if (!submission.formFieldValues) return [];
  const instanceIds = new Set<string>();
  submission.formFieldValues.forEach((fv) => {
    if (fv.groupInstanceId && groupFieldIds.has(fv.taskFieldId)) {
      instanceIds.add(fv.groupInstanceId);
    }
  });
  return Array.from(instanceIds);
};

export const getGroupFieldValue = (
  submission: { formFieldValues?: FormFieldValue[] },
  taskFieldId: string,
  groupInstanceId: string
): string => {
  const fieldValue = submission.formFieldValues?.find(
    (fv) => fv.taskFieldId === taskFieldId && fv.groupInstanceId === groupInstanceId
  );
  return fieldValue?.value || '-';
};

export const buildExpandedColumns = (
  formFields: FormFieldConfig[],
  submissions: Array<{ formFieldValues?: FormFieldValue[] }>
): ExpandedFormFieldConfig[] => {
  const groups = new Map<string, FormFieldConfig[]>();
  const nonGroupFields: FormFieldConfig[] = [];

  formFields.forEach((field) => {
    if (field.groupFormItemId) {
      const existing = groups.get(field.groupFormItemId) || [];
      existing.push(field);
      groups.set(field.groupFormItemId, existing);
    } else {
      nonGroupFields.push(field);
    }
  });

  if (groups.size === 0) {
    return formFields.map((f) => ({ ...f, instanceIndex: 0, expandedName: f.name }));
  }

  const maxInstancesPerGroup = new Map<string, number>();
  groups.forEach((fields, groupFormItemId) => {
    const groupFieldIds = new Set(fields.map((f) => f.taskFieldId));
    let maxInstances = 0;
    submissions.forEach((submission) => {
      const instanceCount = getGroupInstanceIds(submission, groupFieldIds).length;
      if (instanceCount > maxInstances) maxInstances = instanceCount;
    });
    maxInstancesPerGroup.set(groupFormItemId, Math.max(maxInstances, 1));
  });

  const result: ExpandedFormFieldConfig[] = [];
  const fieldOrder = [...formFields];

  const processedGroups = new Set<string>();

  fieldOrder.forEach((field) => {
    if (!field.groupFormItemId) {
      result.push({ ...field, instanceIndex: 0, expandedName: field.name });
    } else if (!processedGroups.has(field.groupFormItemId)) {
      processedGroups.add(field.groupFormItemId);
      const groupFields = groups.get(field.groupFormItemId) || [];
      const maxInst = maxInstancesPerGroup.get(field.groupFormItemId) || 1;
      const groupLabel = field.groupLabel || field.groupTitle || 'Entry';

      for (let i = 0; i < maxInst; i++) {
        groupFields.forEach((gf) => {
          result.push({
            ...gf,
            instanceIndex: i,
            expandedName: maxInst === 1 ? `${groupLabel} - ${gf.name}` : `${groupLabel} ${i + 1} - ${gf.name}`,
          });
        });
      }
    }
  });

  return result;
};

export interface GroupedFieldValues {
  groupInstanceId: string;
  values: FormSubmissionFieldValue[];
}

export const groupFieldValuesByInstance = (
  fieldValues: FormSubmissionFieldValue[],
  groupFieldIds: Set<string>
): { ungrouped: FormSubmissionFieldValue[]; grouped: Map<string, GroupedFieldValues> } => {
  const ungrouped: FormSubmissionFieldValue[] = [];
  const grouped = new Map<string, GroupedFieldValues>();

  fieldValues.forEach((fv) => {
    if (fv.groupInstanceId && groupFieldIds.has(fv.taskFieldId)) {
      const existing = grouped.get(fv.groupInstanceId);
      if (existing) {
        existing.values.push(fv);
      } else {
        grouped.set(fv.groupInstanceId, { groupInstanceId: fv.groupInstanceId, values: [fv] });
      }
    } else {
      ungrouped.push(fv);
    }
  });

  return { ungrouped, grouped };
};
